import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {SampleDetails} from 'constants/SampleConstants';
import {MAX_NEW_ROW_COUNT, MIN_NEW_ROW_COUNT} from 'notebook/data-tables/DataTableConstants';
import dedupeSampleDetails from 'notebook/sample-propagation/util/dedupeSampleDetails';
import {SampleDescriptor} from 'notebook/sample-propagation/util/getSampleDescriptor';
import getSampleDetailsName from 'notebook/sample-propagation/util/getSampleDetailsName';
import getSampleKey from 'notebook/sample-propagation/util/getSampleKey';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import NumericInput from 'ui-library/inputs/NumericInput';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {MODAL_WIDTH} from 'ui-library/ModalConstants';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import {ElementMouseEvent, NumericInputChangeEvent} from 'util/Types';
import OptionalContainerLinkWithLocation from 'workflows/stage-entries/components/OptionalContainerLinkWithLocation';
import {SampleDetailsWithOptionalStageRunSample} from 'workflows/util/StageRunUtil';

const INVALID_ROW_COUNT = 'INVALID_ROW_COUNT';

/**
 * Modal that allows to add samples or plates to a structured table from other tables in the entry.
 *
 * It is "legacy" as we have a new set of components for performing the same operations on newer
 * tables (built on the new StructuredTable framework). The legacy modal is still in use for old tables.
 */

export interface LegacySamplesSource {
  name: string;
  icon: BenchlingIconName;
  sampleDetails: Array<SampleDetailsWithOptionalStageRunSample>;
}

function getFilteredSampleDetails(
  sources: Array<LegacySamplesSource>,
  sourceTableFilter: string | null
): Array<SampleDetailsWithOptionalStageRunSample> {
  if (sources.length === 0 || !sourceTableFilter) {
    return [];
  }
  const source = sources.find((s) => s.name === sourceTableFilter);
  assert(source != null, `Unexpected source filter ${sourceTableFilter}`);
  return dedupeSampleDetails(source.sampleDetails);
}

function getDefaultSourceTableFilter(sources: Array<LegacySamplesSource> | null): string | null {
  if (!sources || sources.length === 0) {
    return null;
  }
  const firstNonEmpty = sources.find((source) => source.sampleDetails.length > 0);
  return (firstNonEmpty || sources[0]).name;
}

interface RowCounts {
  [sampleKey: string]: number | 'INVALID_ROW_COUNT';
}

interface AddSamplesColGroupProps {
  enableRowCounts: boolean;
  samplesHaveStageRuns: boolean;
}

function AddSamplesColGroup(props: AddSamplesColGroupProps): JSX.Element {
  // We want to span the entire width with specific column widths.
  const CHECKBOX_COL_WIDTH = 40;
  const SAMPLE_ID_COL_WIDTH = 180;
  const STAGE_RUN_COL_WIDTH = 100;
  const NUMBER_OF_ROWS_COL_WIDTH = 130;
  let leftoverColumnWidth = MODAL_WIDTH - CHECKBOX_COL_WIDTH - SAMPLE_ID_COL_WIDTH - STAGE_RUN_COL_WIDTH;
  if (props.enableRowCounts) {
    leftoverColumnWidth -= NUMBER_OF_ROWS_COL_WIDTH;
  }
  return (
    <colgroup>
      <col style={{width: CHECKBOX_COL_WIDTH}} />
      <col style={{width: SAMPLE_ID_COL_WIDTH}} />
      <col style={{width: leftoverColumnWidth}} />
      {props.samplesHaveStageRuns && <col style={{width: STAGE_RUN_COL_WIDTH}} />}
      {props.enableRowCounts && <col style={{width: NUMBER_OF_ROWS_COL_WIDTH}} />}
    </colgroup>
  );
}

interface AddSamplesDropdownSourceMenuProps {
  scrollable: boolean;
  sourceTableFilter: string | null;
  sources: Array<LegacySamplesSource>;
  selectNewSource(source: LegacySamplesSource): void;
}

function AddSamplesDropdownSourceMenu(props: AddSamplesDropdownSourceMenuProps): JSX.Element {
  return (
    <Menu
      scrollable={true}
      height={Menu.HEIGHTS.MEDIUM}
      width={Menu.WIDTHS.FULL}
      value={props.sourceTableFilter}
    >
      {props.sources.map((source, index) => (
        <Option
          key={source.name + index}
          value={source.name}
          onSelect={() => {
            props.selectNewSource(source);
          }}
          icon={source.icon}
        >
          {source.name}
        </Option>
      ))}
    </Menu>
  );
}

function AddSamplesErrorMessage(props: {error?: string; sampleDescriptor: SampleDescriptor}): JSX.Element {
  return (
    <Row>
      <Col md={12}>
        <p className="u-tiny-spacing-top">
          {props.error || `No ${props.sampleDescriptor.plural} are available.`}
        </p>
      </Col>
    </Row>
  );
}

const isRowCountInvalid = (rowCount: number | 'INVALID_ROW_COUNT'): boolean =>
  rowCount === INVALID_ROW_COUNT ||
  !Number.isInteger(rowCount) ||
  rowCount < MIN_NEW_ROW_COUNT ||
  rowCount > MAX_NEW_ROW_COUNT;

export interface AddSamplesModalProps {
  title: string;
  enableRowCounts: boolean;
  samplesHaveStageRuns: boolean;
  // null value indicates that data is still loading
  sources: Array<LegacySamplesSource> | null;
  onRequestClose: () => void;
  noSamplesMessage?: string;
  // What is the user selecting? Pass a noun. (Default is "sample/samples")
  sampleDescriptor?: SampleDescriptor | null;
  omitDefaultSource?: boolean;
  onSamplesSelected: (selectedSampleDetails: Array<SampleDetails>) => void;
}

interface SelectionList {
  [index: number]: boolean;
}

interface State {
  // We track which samples the user has manually DE-selected, to ensure that new data
  // coming in does not override their choices (and to make caching easy)
  deselectedIndices: SelectionList;
  sourceTableFilter: string | null;
  rowCountUserInputs: RowCounts;
  deselectionCache: {[sourceName: string]: SelectionList};
}

export class LegacyAddSamplesModal extends React.Component<AddSamplesModalProps, State> {
  override state: State = {
    deselectedIndices: {},
    sourceTableFilter: null,
    rowCountUserInputs: {},
    deselectionCache: {},
  };

  static getDerivedStateFromProps(nextProps: AddSamplesModalProps, prevState: State): Partial<State> | null {
    return prevState.sourceTableFilter == null && !nextProps.omitDefaultSource
      ? {sourceTableFilter: getDefaultSourceTableFilter(nextProps.sources)}
      : null;
  }

  _getSampleDescriptor = (): SampleDescriptor =>
    this.props.sampleDescriptor || {
      singular: 'sample',
      plural: 'samples',
      title: 'Sample',
    };

  _handleSubmit = (): void => {
    const filteredSampleDetails = getFilteredSampleDetails(this.props.sources!, this.state.sourceTableFilter);
    const selectedSampleDetails = _.chain(filteredSampleDetails)
      .map((details, i) => {
        const rowCount = this._getRowCount(getSampleKey(details));
        assert(!isRowCountInvalid(rowCount), 'Invalid row count', {rowCount, details, i});
        // eslint-disable-next-line no-restricted-syntax
        return _.times(rowCount, () => details);
      })
      .filter((details, i) => !this.state.deselectedIndices[i])
      .flatten()
      .value();
    this.props.onSamplesSelected(selectedSampleDetails);
    this.props.onRequestClose();
  };

  _areAnySamplesSelected(): boolean {
    const numberOfDeselectedSamples = Object.values(this.state.deselectedIndices).filter((b) =>
      Boolean(b)
    ).length;
    const sources = this.props.sources;
    const numberOfSelectableSamples =
      sources == null ? 0 : getFilteredSampleDetails(sources, this.state.sourceTableFilter).length;
    return numberOfDeselectedSamples < numberOfSelectableSamples;
  }

  _getIsDisabled(): boolean {
    return (
      !this._areAnySamplesSelected() ||
      Object.values(this.state.rowCountUserInputs).some((rowCount) => isRowCountInvalid(rowCount))
    );
  }

  _getButtons(): Array<ModalButton> {
    return [
      {
        text: 'Add',
        style: ButtonStyle.PRIMARY,
        disabled: this._getIsDisabled(),
        onClick: this._handleSubmit,
        key: 'create',
        dataTestAttrs: {component: 'LegacyAddSamplesModal', element: 'add', key: ModalButtonKey.ADD},
      },
    ];
  }

  _getRowCount(sampleKey: string): number {
    return this.state.rowCountUserInputs[sampleKey] == null ||
      this.state.rowCountUserInputs[sampleKey] === INVALID_ROW_COUNT
      ? 1
      : this.state.rowCountUserInputs[sampleKey];
  }

  _renderSelectionRowsTable(sources: Array<LegacySamplesSource>): JSX.Element {
    const filteredSampleDetails = getFilteredSampleDetails(sources, this.state.sourceTableFilter);
    const rows = filteredSampleDetails.map((sampleDetails, i) => {
      const {stageRun, containerInfo} = sampleDetails;
      const sampleKey = getSampleKey(sampleDetails);
      return (
        <tr
          className="stageEntry-addSamples-bodyRow"
          key={sampleKey}
          onClick={() => {
            this._handleClickCheckbox(i, !this.state.deselectedIndices[i]);
          }}
        >
          <td className="stageEntry-addSamples-checkboxCell">
            <input
              checked={!this.state.deselectedIndices[i]}
              className={css(styles.checkbox)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                this._handleClickCheckbox(i, event.target.checked);
              }}
              type="checkbox"
            />
          </td>
          <td>{getSampleDetailsName(sampleDetails)}</td>
          <td>
            <OptionalContainerLinkWithLocation
              containerId={containerInfo == null ? null : containerInfo.id}
            />
          </td>
          {this.props.samplesHaveStageRuns && <td>{stageRun == null ? 'N/A' : stageRun.name}</td>}
          {this.props.enableRowCounts && (
            <td className="stageEntry-addSamples-numericInput">
              <NumericInput
                isDisabled={Boolean(this.state.deselectedIndices[i])}
                min={MIN_NEW_ROW_COUNT}
                max={MAX_NEW_ROW_COUNT}
                onChange={(e: NumericInputChangeEvent) => {
                  this.setState({
                    rowCountUserInputs: {
                      ...this.state.rowCountUserInputs,
                      [sampleKey]: e.target.numericValue || INVALID_ROW_COUNT,
                    },
                  });
                }}
                onClick={(e: ElementMouseEvent) => {
                  // Prevent clicking on the row and toggling the selection state
                  e.stopPropagation();
                }}
                value={this._getRowCount(sampleKey)}
              />
            </td>
          )}
        </tr>
      );
    });

    return (
      <div className="stageEntry-addSamples-bodyContainer">
        <table>
          <AddSamplesColGroup
            enableRowCounts={this.props.enableRowCounts}
            samplesHaveStageRuns={this.props.samplesHaveStageRuns}
          />
          <tbody>{rows}</tbody>
        </table>
      </div>
    );
  }

  _handleClickCheckbox(i: number, value: boolean): void {
    this.setState({
      deselectedIndices: {
        ...this.state.deselectedIndices,
        [i]: !value,
      },
    });
  }

  _renderHeaderCheckbox(sources: Array<LegacySamplesSource>): JSX.Element {
    return (
      <IndeterminateCheckbox
        items={Array.from(getFilteredSampleDetails(sources, this.state.sourceTableFilter).keys())}
        selectedMap={(i) => !this.state.deselectedIndices[i]}
        onChange={this._handleClickHeaderCheckbox}
      />
    );
  }

  _handleClickHeaderCheckbox = (): void => {
    const filteredSampleDetails = getFilteredSampleDetails(this.props.sources!, this.state.sourceTableFilter);
    if (Array.from(filteredSampleDetails.keys()).some((i) => Boolean(this.state.deselectedIndices[i]))) {
      this.setState({
        deselectedIndices: {},
      });
    } else {
      this.setState({
        deselectedIndices: _.object(Array.from(filteredSampleDetails.keys()).map((i) => [i, true])),
      });
    }
  };

  _selectNewSource(source: LegacySamplesSource): void {
    // Quick note on caching: when the user selects a new source, we
    //  * load the new deselectedIndices from the cache (for the new source)
    //  * put the current deselectedIndices into the cache (for the old source)
    const cachedSelections = this.state.deselectionCache[source.name];
    const newState = {
      deselectedIndices: cachedSelections || {},
      sourceTableFilter: source.name,
      rowCountUserInputs: {},
      deselectionCache: {
        ...this.state.deselectionCache,
        ...(this.state.sourceTableFilter && {
          [this.state.sourceTableFilter]: this.state.deselectedIndices,
        }),
      },
    };
    this.setState(newState);
  }

  _renderModalContents(): JSX.Element {
    const sources = this.props.sources;
    if (sources == null) {
      return <CenteredHorizontalThrobber />;
    }
    const filteredSampleDetails = getFilteredSampleDetails(sources, this.state.sourceTableFilter);

    return (
      <>
        <Row>
          <Col md={12}>
            <SelectDropdown
              menu={
                <AddSamplesDropdownSourceMenu
                  scrollable={true}
                  sourceTableFilter={this.state.sourceTableFilter}
                  sources={sources}
                  selectNewSource={(source) => {
                    this._selectNewSource(source);
                  }}
                />
              }
              displayName={this.state.sourceTableFilter || 'Select source'}
              disabled={sources.length === 0}
              autoFocus={true}
            />
          </Col>
        </Row>
        {filteredSampleDetails.length === 0 ? (
          <AddSamplesErrorMessage
            error={this.props.noSamplesMessage}
            sampleDescriptor={this._getSampleDescriptor()}
          />
        ) : (
          <Row>
            <table>
              <AddSamplesColGroup
                enableRowCounts={this.props.enableRowCounts}
                samplesHaveStageRuns={this.props.samplesHaveStageRuns}
              />
              <tbody>
                <tr className="stageEntry-addSamples-headerRow">
                  <td className="stageEntry-addSamples-checkboxCell">
                    {this._renderHeaderCheckbox(sources)}
                  </td>
                  <td className="u-capitalize">{this._getSampleDescriptor().singular}</td>
                  <td>Container</td>
                  {this.props.samplesHaveStageRuns && <td>Stage Run</td>}
                  {this.props.enableRowCounts && <td>Number of rows</td>}
                </tr>
              </tbody>
            </table>
            {this._renderSelectionRowsTable(sources)}
          </Row>
        )}
      </>
    );
  }

  override render(): JSX.Element {
    return (
      <Modal title={this.props.title} onRequestClose={this.props.onRequestClose} buttons={this._getButtons()}>
        {this._renderModalContents()}
      </Modal>
    );
  }
}

export const styles = StyleSheet.create({
  checkbox: {
    accentColor: BLUE,
  },
});

export default LegacyAddSamplesModal;
